'use client';

import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { EFeedback } from '@/types';
import { useMessageFeedback } from '@/_state/mutations';

interface FeedbackButtonsProps {
	messageId: string;
	currentFeedback?: EFeedback | null;
	disabled?: boolean;
}

/**
 * Thumbs up/down feedback buttons for AI responses
 *
 * - Click to set feedback
 * - Click again to toggle off (set to null)
 * - Optimistic UI updates for instant feedback
 */
export default function FeedbackButtons({ messageId, currentFeedback, disabled = false }: FeedbackButtonsProps) {
	const { mutate: updateFeedback, isPending } = useMessageFeedback();

	const handleFeedback = (feedbackType: EFeedback) => {
		// Toggle off if clicking the same feedback type
		const newFeedback = currentFeedback === feedbackType ? null : feedbackType;
		updateFeedback({ messageId, feedback: newFeedback });
	};

	const isPositive = currentFeedback === EFeedback.POSITIVE;
	const isNegative = currentFeedback === EFeedback.NEGATIVE;
	const isDisabled = disabled || isPending;

	return (
		<div className='flex items-center gap-1'>
			<button
				type='button'
				onClick={() => handleFeedback(EFeedback.POSITIVE)}
				disabled={isDisabled}
				className={`rounded-md p-1.5 transition-all ${
					isPositive
						? 'bg-emerald-500/20 text-emerald-400'
						: 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
				} ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
				title='Helpful response'
				aria-label='Mark as helpful'
				aria-pressed={isPositive}
			>
				<ThumbsUp
					className='h-4 w-4'
					strokeWidth={1.5}
				/>
			</button>
			<button
				type='button'
				onClick={() => handleFeedback(EFeedback.NEGATIVE)}
				disabled={isDisabled}
				className={`rounded-md p-1.5 transition-all ${
					isNegative ? 'bg-red-500/20 text-red-400' : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
				} ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
				title='Not helpful'
				aria-label='Mark as not helpful'
				aria-pressed={isNegative}
			>
				<ThumbsDown
					className='h-4 w-4'
					strokeWidth={1.5}
				/>
			</button>
		</div>
	);
}
